'use client';

import React, { memo, useState, useEffect, useMemo } from 'react';
import { getDynamicImageSource, getStaticImageSource } from 'app/libs/url';
import './MediaBackground.scss';

interface MediaBackgroundBasicPropTypes {
    source?: string;
    defaultSource?: string;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
}

interface MediaBackgroundPropTypes extends MediaBackgroundBasicPropTypes {
    type: string;
    mediaType: 'image' | 'video';
}

export const MediaPictureBackground: React.FC<MediaBackgroundBasicPropTypes> = (
    props
) => {
    const { source: originalSource, defaultSource, setIsLoading } = props;

    const source = originalSource
        ? getDynamicImageSource(originalSource)
        : getStaticImageSource(defaultSource);

    useEffect(() => {
        function checkImageBrowserCache() {
            const img = new Image();

            if (source?.[0]?.srcSet) {
                img.srcset = source?.[0]?.srcSet;
            }

            if (source.src) {
                img.src = source.src;
            }

            img.onload = () => {
                setIsLoading(false);
            };
        }

        checkImageBrowserCache();
    }, [source]);

    return (
        <picture className="media-background__picture">
            <source srcSet={source.srcSet} type={source.type} />
            <img
                className="media-background__image"
                src={source.src}
                alt="media background"
                onLoad={() => setIsLoading(false)}
            />
        </picture>
    );
};

export const MediaVideoBackground: React.FC<MediaBackgroundBasicPropTypes> = (props) => {
    const { source: originalSource, setIsLoading } = props;

    const posterBaseUrl = useMemo(
        () =>
            originalSource
                ?.replace('video', 'image')
                .replace('library', 'video_preview')
                .replace('.mp4', '_preview_0x0@2x.avif'),
        [originalSource]
    );

    return (
        <video
            className="media-background__video"
            src={originalSource}
            poster={posterBaseUrl}
            muted
            autoPlay
            playsInline
            onLoadedData={() => setIsLoading(false)}
            loop
        />
    );
};

const shouldMemorize = (
    prev: MediaBackgroundPropTypes,
    next: MediaBackgroundPropTypes
) => {
    return prev.source === next.source;
};

const _MediaBackground: React.FC<MediaBackgroundPropTypes> = (props) => {
    const { source, mediaType, defaultSource } = props;

    const [isLoading, setIsLoading] = useState(true);

    if (!source && !defaultSource) {
        return null;
    }

    return (
        <div className="media-background" data-shop-ui-component-type={mediaType}>
            {isLoading && <div className="skeleton" />}
            {mediaType === 'video' && (
                <MediaVideoBackground
                    source={source}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            )}
            {mediaType === 'image' && (
                <MediaPictureBackground
                    source={source}
                    defaultSource={defaultSource}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            )}
        </div>
    );
};

export const MediaBackground = memo(_MediaBackground, shouldMemorize);
